import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { allowedTransitions, LIFECYCLE, type LifecycleStatus } from '../../lib/appointmentLifecycle';
import { getSdrAppointments, getRdvForClients, getMyFavoriteIds, getAgendaSdrs, advanceAppointment, contractTypeOf, getPendingDeletionRequests, decideAppointmentDeletion, type RdvRow, type SdrOption, type DeletionRequest } from '../../lib/refonteApi';
import RdvTabs from '../../components/refonte/RdvTabs';
import { NqReasonModal } from '../../components/refonte/NqReasonPicker';
import StatusBadge from '../../components/refonte/StatusBadge';
import SourceBadge from '../../components/refonte/SourceBadge';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const QUICK: LifecycleStatus[] = ['held', 'no_show', 'qualified', 'not_qualified'];
const QUICK_SHORT: Partial<Record<LifecycleStatus, string>> = { held: '✓ Honoré', no_show: 'No-show', qualified: '✓ Qualifié', not_qualified: 'Non qualifié' };
const isoToday = () => new Date().toISOString().slice(0, 10);

// RDV À STATUER — pour un SDR : les RDV de SES clients en favoris (★). Deux files :
// « À relancer » (date passée, encore posé/confirmé → chasser l'issue) et « À statuer » (décision).
const RdvAStatuerPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'sdr';
  const isSdr = role === 'sdr';
  const isManager = !isSdr;

  const [sdrOptions, setSdrOptions] = useState<SdrOption[]>([]);
  const [sdrId, setSdrId] = useState<string | null>(null);
  const [rows, setRows] = useState<RdvRow[]>([]);
  const [noFav, setNoFav] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (isManager) getAgendaSdrs().then(o => { setSdrOptions(o); setSdrId(p => p ?? o[0]?.id ?? null); }).catch(() => {}); }, [isManager]);
  // File des demandes de suppression (admins) — décision 01/10 enfin câblée (retour Démèt).
  const isAdmin = role === 'admin' || role === 'super_admin';
  const [delReqs, setDelReqs] = useState<DeletionRequest[]>([]);
  const loadDelReqs = useCallback(() => { if (isAdmin) getPendingDeletionRequests().then(setDelReqs).catch(() => {}); }, [isAdmin]);
  useEffect(() => { loadDelReqs(); }, [loadDelReqs]);
  const decide = async (id: string, ok: boolean) => {
    try { await decideAppointmentDeletion(id, ok); loadDelReqs(); load(); }
    catch (e: any) { setErr(e.message); }
  };

  const load = useCallback(async () => {
    setLoading(true); setErr(null); setNoFav(false);
    try {
      if (isSdr) {
        const favIds = await getMyFavoriteIds(profile?.user_id ?? '');
        if (!favIds.length) {
          // FALLBACK (audit UX 09/10) : 3 SDR seulement utilisent les favoris → cette page était
          // VIDE pour les autres, qui ne voyaient jamais leur backlog (stock de 1 516 non statués).
          // Sans ★ : les RDV dont le SDR est OWNER. Le bandeau « astuce » reste affiché.
          setRows(await getSdrAppointments(profile?.user_id ?? undefined));
          setNoFav(true);
        }
        else setRows(await getRdvForClients(favIds));
      } else {
        setRows(await getSdrAppointments(sdrId ?? undefined));
      }
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [isSdr, sdrId, profile?.user_id]);
  useEffect(() => { load(); }, [load]);

  const [nqTarget, setNqTarget] = useState<RdvRow | null>(null);
  const submitNq = async (reason: string, note?: string) => {
    if (!nqTarget) return;
    setBusy(nqTarget.id); setErr(null);
    try { await advanceAppointment(nqTarget.id, 'not_qualified', { reason, note }); setNqTarget(null); await load(); }
    catch (e: any) { setErr(e.message); }
    setBusy(null);
  };

  const quickAct = async (rdv: RdvRow, to: LifecycleStatus) => {
    setBusy(rdv.id); setErr(null);
    try {
      // motif « non qualifié » = liste fermée → modale dédiée (plus de texte libre, audit 08/10)
      if (to === 'not_qualified') { setNqTarget(rdv); setBusy(null); return; }
      await advanceAppointment(rdv.id, to, { icpConformant: to === 'qualified' ? true : undefined });
      await load();
    } catch (e: any) { setErr(e.message); }
    setBusy(null);
  };

  const today = isoToday();
  const pending = (s?: LifecycleStatus | null) => s == null || s === 'posed' || s === 'confirmed';
  // À relancer : l'heure/le jour est passé mais toujours posé/confirmé (issue inconnue).
  const toRelance = rows.filter(r => pending(r.lifecycle_status as LifecycleStatus) && !!r.date && r.date < today);
  // À statuer : honoré (→ qualifier) ou posé/confirmé aujourd'hui/à venir.
  const toStatuer = rows.filter(r => !toRelance.includes(r) && ['posed', 'confirmed', 'held'].includes((r.lifecycle_status ?? '') as string));

  const copySms = async (rdv: RdvRow) => {
    const prenom = (rdv.prospect_name || '').trim().split(' ')[0] || 'Bonjour';
    const sms = `Bonjour ${prenom}, je reviens vers vous suite à notre échange${rdv.clients?.name ? ` au sujet de ${rdv.clients.name}` : ''}. Êtes-vous toujours disponible pour notre rendez-vous ? Je reste à votre écoute. Belle journée.`;
    try { await navigator.clipboard.writeText(sms); setCopied(rdv.id); setTimeout(() => setCopied(c => c === rdv.id ? null : c), 1800); } catch { /* clipboard indispo */ }
  };

  const Row: React.FC<{ rdv: RdvRow; overdue?: boolean }> = ({ rdv, overdue }) => {
    const quick = allowedTransitions(rdv.lifecycle_status ?? null, role, contractTypeOf(rdv)).filter(t => QUICK.includes(t));
    const phone = (rdv as any).phone as string | null;
    return (
      <div className="py-2.5 flex items-center gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <Link to={`/app/portail/rdv/${rdv.id}?c=${rdv.client_id ?? ''}`} className="text-[13px] font-semibold hover:text-hook-rose">{rdv.prospect_name || 'Prospect'}{rdv.company_name && <span className="text-dim font-normal"> · {rdv.company_name}</span>}</Link>
          <div className="font-mono text-[10px] text-faint">{rdv.clients?.name || '—'} · {rdv.date || 'sans date'}{overdue && <span className="text-st-warn"> · en retard</span>}</div>
        </div>
        <StatusBadge status={rdv.lifecycle_status ?? null} />
                  <SourceBadge source={(rdv as any).source} compact />
        <div className="flex gap-1 flex-wrap items-center">
          {/* Actions de relance */}
          <Link to={`/app/portail/rdv/${rdv.id}?c=${rdv.client_id ?? ''}`} title="Voir le rendez-vous" className="text-[10.5px] font-semibold px-2 py-0.5 rounded border border-line-2 text-dim hover:border-ink whitespace-nowrap">Voir le RDV</Link>
          {phone && <a href={`tel:${phone.replace(/\s/g, '')}`} title={`Appeler ${phone}`} className="text-[10.5px] font-semibold px-2 py-0.5 rounded border border-line-2 text-dim hover:border-ink whitespace-nowrap">📞 Appeler</a>}
          <button onClick={() => copySms(rdv)} title="Copier un SMS de relance" className="text-[10.5px] font-semibold px-2 py-0.5 rounded border border-line-2 text-dim hover:border-ink whitespace-nowrap">{copied === rdv.id ? '✓ Copié' : '✉ SMS'}</button>
          <span className="w-px h-4 bg-line-2 mx-0.5" />
          {quick.map(t => (
            <button key={t} disabled={busy === rdv.id} onClick={() => quickAct(rdv, t)}
              className={`text-[10.5px] font-semibold px-2 py-0.5 rounded border disabled:opacity-40 whitespace-nowrap ${t === 'held' || t === 'qualified' ? 'border-hook-rose/40 text-hook-rose hover:bg-hook-rose/5' : 'border-line-2 text-dim hover:border-ink'}`}>{QUICK_SHORT[t] ?? LIFECYCLE[t].label}</button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {nqTarget && <NqReasonModal busy={busy === nqTarget.id} onSubmit={submitNq} onCancel={() => setNqTarget(null)} />}
      {isAdmin && delReqs.length > 0 && (
        <div className="mb-4 bg-panel border border-st-warn/40 rounded-card p-4">
          <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-st-warn mb-2">Demandes de suppression de RDV ({delReqs.length})</h2>
          <div className="divide-y divide-line">
            {delReqs.map(r => (
              <div key={r.id} className="py-2 flex items-center gap-3 flex-wrap text-[12.5px]">
                <span className="font-semibold">{r.prospect || 'RDV'}</span>
                <span className="text-dim">{r.clientName || ''} · {r.rdvDate || 'sans date'}</span>
                <span className="text-faint italic flex-1">« {r.reason} » — {r.requesterName || 'interne'}</span>
                <button onClick={() => decide(r.id, true)} className="text-[11.5px] font-semibold px-2.5 py-1 rounded border border-st-bad/40 text-st-bad hover:bg-st-bad/5">Supprimer (annule la saisie)</button>
                <button onClick={() => decide(r.id, false)} className="text-[11.5px] px-2.5 py-1 rounded border border-line-2 text-dim hover:border-ink">Rejeter</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">RDV à statuer</h1>
      <RdvTabs />
        {isManager && (
          <label className="flex items-center gap-1.5 text-[12px] text-dim">
            <span className="font-mono text-[9px] uppercase tracking-widest text-faint">SDR</span>
            <select value={sdrId ?? ''} onChange={e => setSdrId(e.target.value || null)} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] max-w-[220px]">
              <option value="">Tous</option>
              {sdrOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        )}
      </div>
      <p className="text-dim text-[13px] mb-4">{isSdr ? 'Les RDV de tes clients en favoris (★) : à relancer si l\'issue est inconnue, puis à statuer (honoré / no-show, qualifié / non).' : 'RDV en attente d\'une décision : honoré / no-show, puis qualifié / non qualifié.'}</p>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2">{err}</div>}

      {noFav && (
        <div className="mb-4 text-[12px] text-dim bg-panel-2 border border-line rounded-lg px-3 py-2">
          💡 Astuce : ajoute tes clients en favori (★) depuis le registre pour suivre TOUS leurs RDV ici.
          En attendant, voici les rendez-vous dont tu es l'owner.
        </div>
      )}
      {loading ? <SkeletonRows count={6} /> : (
        <div className="space-y-4">
          {/* Relances */}
          <div className="bg-panel border border-line rounded-card p-5">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-st-warn">⏰ À relancer</h2>
              <span className="font-mono text-[11px] text-faint">{toRelance.length} RDV</span>
            </div>
            {toRelance.length === 0 ? <p className="text-[12.5px] text-dim">Aucune relance en attente. 👍</p> : (
              <div className="divide-y divide-line">{toRelance.map(r => <Row key={r.id} rdv={r} overdue />)}</div>
            )}
          </div>
          {/* À statuer */}
          <div className="bg-panel border border-line rounded-card p-5">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim">À statuer</h2>
              <span className="font-mono text-[11px] text-faint">{toStatuer.length} RDV</span>
            </div>
            {toStatuer.length === 0 ? <EmptyState title="Rien à statuer" hint="Tous les RDV sont à jour. 👏" /> : (
              <div className="divide-y divide-line">{toStatuer.map(r => <Row key={r.id} rdv={r} />)}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RdvAStatuerPage;
